import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import OtpInput from "otp-input-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { auth } from "../components/firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { toast } from "react-hot-toast";
import AxiosInstance from "../context/axiosInstance";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { getFcmToken } from "../components/firebase";
import SignupModalContent from "./SignupModal"; // Renamed for clarity
import { ShieldCheck, X, Loader2 } from "lucide-react";

const LoginModal = ({ setShowModal, city, pincode }) => {
  const [otp, setOtp] = useState("");
  const [ph, setPh] = useState("");
  const [loading, setLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [userExists, setUserExists] = useState(true); // Default to true (login flow)
  const navigate = useNavigate();
  const { setAccessToken, setRefreshToken, setUserID, setIsAuthenticated } =
    useAppContext();


  // Your reCAPTCHA logic is correct for Firebase Phone Auth. No changes needed here.
  useEffect(() => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        { size: "invisible" }
      );
    }
  }, []);

  const checkUserExists = async () => {
    if (ph.length < 10) {
      return toast.error("Please enter a valid phone number.");
    }
    setLoading(true);
    try {
      const formattedPh = ph.startsWith("+") ? ph : `+${ph}`;
      const response = await AxiosInstance.post("/auth/users/lookup", {
        mobile_number: formattedPh,
      });

      if (response.data.exists) {
        setUserExists(true);
        setUserID(response.data.userID);
        sendOTP(formattedPh);
      } else {
        setUserExists(false); // Switch to the signup flow
      }
    } catch (error) {
      toast.error("Error checking user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const sendOTP = (formattedPh) => {
    setLoading(true);
    const appVerifier = window.recaptchaVerifier;
    signInWithPhoneNumber(auth, formattedPh, appVerifier)
      .then((confirmationResult) => {
        window.confirmationResult = confirmationResult;
        setShowOTP(true);
        toast.success("OTP sent successfully!");
      })
      .catch((err) => {
        console.error("OTP Send Error:", err);
        toast.error("Failed to send OTP. Please refresh and try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

const onOTPVerify = async () => {
  if (otp.length < 6) {
    return toast.error("Please enter the 6-digit OTP.");
  }
  setLoading(true);
  try {
    // 1. Confirm OTP with Firebase
    await window.confirmationResult.confirm(otp);
    toast.success("OTP Verified! Logging in...");

    // 2. Login to your backend to get tokens
    const formattedPh = ph.startsWith("+") ? ph : `+${ph}`;
    const response = await AxiosInstance.post("/auth/sessions", {
      mobile_number: formattedPh,
    });

    // ✅ 3. Destructure the CORRECT fields from the response
    const { accessToken, refreshToken, profile } = response.data;

    // ✅ 4. Set all the values in the context
    setAccessToken(accessToken);
    setRefreshToken(refreshToken);
    setUserID(profile.user_id); // Get from the nested profile object
    setIsAuthenticated(true);

    // 5. Save FCM token (your existing logic is correct)
    const fcmToken = await getFcmToken();
    if (fcmToken) {
      await AxiosInstance.post("/auth/fcm-tokens", {
        token: fcmToken,
        phone: formattedPh,
      });
    }

    // 6. Finalize UI
    setShowModal(false);
    navigate("/app");
  } catch (error) {
    console.error("Login/OTP Verification Error:", error);
    toast.error("Invalid OTP or login failed. Please try again.");
  } finally {
    setLoading(false);
  }
};


  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  };

  const contentVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 },
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-md p-4 transition-all duration-300">
      <motion.div
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="relative w-full max-w-md bg-surface/90 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl shadow-black/50 p-8"
      >
        <button
          onClick={() => setShowModal(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors bg-white/5 hover:bg-white/10 rounded-full p-2"
        >
          <X size={20} />
        </button>

        <AnimatePresence mode="wait">
          {!userExists ? (
            <motion.div
              key="signup"
              variants={contentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <SignupModalContent
                setShowModal={setShowModal}
                phone={ph.startsWith("+") ? ph : `+${ph}`}
                city={city}
                pincode={pincode}
                setUser={setUserID}
              />
            </motion.div>
          ) : showOTP ? (
            <motion.div
              key="otp"
              variants={contentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="flex flex-col items-center text-center"
            >
              <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mb-6 ring-4 ring-primary-500/10">
                <ShieldCheck size={32} className="text-primary-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Enter OTP</h2>
              <p className="text-gray-400 mb-8 max-w-[80%]">
                We've sent a 6-digit verification code to <span className="text-white font-medium">{ph.startsWith("+") ? ph : `+${ph}`}</span>
              </p>

              <OtpInput
                value={otp}
                onChange={setOtp}
                OTPLength={6}
                otpType="number"
                inputClassName="!w-full !max-w-[3rem] aspect-square bg-white/5 border border-white/10 rounded-xl text-white text-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-all placeholder-gray-600"
                containerClassName="w-full flex gap-3 justify-center"
              />
              <button
                onClick={onOTPVerify}
                disabled={loading}
                className="w-full mt-8 bg-white text-black font-bold py-4 rounded-xl hover:bg-gray-100 transition-all flex items-center justify-center disabled:opacity-50 hover:shadow-lg hover:scale-[1.02]"
              >
                {loading ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  "Verify & Continue"
                )}
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="phone"
              variants={contentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="flex flex-col"
            >
              <h2 className="text-3xl font-black tracking-tighter text-white mb-2">
                Welcome Back
              </h2>
              <p className="text-gray-400 mb-8">
                Enter your phone number to sign in or create an account.
              </p>

              <div className="group relative">
                <PhoneInput
                  country={"in"}
                  value={ph}
                  onChange={setPh}
                  containerClass="!w-full !mb-2"
                  inputClass="!w-full !h-14 !bg-white/5 !border !border-white/10 !text-white !rounded-xl !text-lg !pl-14 focus:!ring-2 focus:!ring-primary-500 focus:!border-primary-500 !transition-all group-hover:!border-white/20"
                  buttonClass="!bg-transparent !border-none !pl-2"
                  dropdownClass="!bg-surface !border !border-white/10 !text-white !rounded-xl !shadow-xl !backdrop-blur-xl"
                  searchClass="!bg-surface !text-white"
                />
              </div>
              
              <button
                onClick={checkUserExists}
                disabled={loading}
                className="w-full mt-6 bg-white text-black font-bold py-4 rounded-xl hover:bg-gray-100 transition-all flex items-center justify-center disabled:opacity-50 hover:shadow-lg hover:scale-[1.02]"
              >
                {loading ? <Loader2 className="animate-spin" /> : "Proceed"}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      {/* This must remain for Firebase reCAPTCHA to work */}
      <div id="recaptcha-container"></div>
    </div>
  );
};

export default LoginModal;
